import { InputFileDetails } from '../input-file-details';
import {
  getHeadersInfoFromCmdLine,
  sendMail,
} from '@/app/features/products/common/services';
import { fieldHeadersValidator } from '@/app/features/products/common/components/file-selection/validators/input-file-validators';
import { delimitedParser } from '@/app/features/products/common/components/file-selection/parsers';
import { ParseRows } from '@/app/features/products/common/components/file-selection/models';
import {
  fetchAndEnforceApiGatewayResponseData,
  HeaderInfo,
  acceptedExtensions,
} from '@ssar-app/domain';
import { FileUploadProps } from '@ssar-app/uikit';

const acceptedExtensionsString = acceptedExtensions
  .map((a) => `.${a}`)
  .join(',');
export const fileExtensionRegex = /(?:\.([^.]+))?$/;

export const StaticUploadProps: FileUploadProps = {
  fileProps: {
    accept: acceptedExtensionsString,
    name: 'file',
    multiple: false,
  },
  showUploadedFiles: false,
};

export const getMLHeaderInfo = (
  parseResponse: ParseRows,
  file: File,
): Promise<HeaderInfo> => {
  return fetchAndEnforceApiGatewayResponseData(() =>
    getHeadersInfoFromCmdLine(parseResponse.data),
  )
    .then((data) => {
      if (data) {
        return Promise.resolve(data.headersInfo);
      } else {
        return Promise.reject('Error while loading file.');
      }
    })
    .catch((error) => {
      const headersInfo: HeaderInfo = {
        hasHeaders: null,
        detectedHeaders: [],
        actualHeaders: [],
      };
      const errorDetails: Record<string, string> = {
        fileName: file.name,
        fileType: 'Locally uploaded',
        error: error.technicalDetails[0],
      };
      sendMail('ML Failure', errorDetails).then((res) => {
        console.log(res);
      });
      return Promise.resolve(headersInfo);
    });
};

export const executeDelimitedParser = (
  headersInfo: HeaderInfo,
  file?: File,
  content?: string[],
  experianFileName?: string,
  clientFileName?: string,
  totalRecords?: number,
  intentType?: string,
  fileId?: string,
): Promise<{ inputfileDetails: InputFileDetails | null; error: string }> => {
  return delimitedParser(file || content?.join('\n') || '', headersInfo, 100)
    .then(async (parseResponse) => {
      const hasEmptyHeaders = headersInfo.actualHeaders.some(
        (header) => !header || !header.trim(),
      );
      if (parseResponse.scannedRowsCount < 1) {
        return Promise.resolve({
          inputfileDetails: null,
          error: 'Selected file does not have any data rows.',
        });
      } else if (
        (parseResponse.headersInfo.hasHeaders &&
          parseResponse.headersInfo.actualHeaders.length <
            parseResponse.data.length) ||
        parseResponse.headersInfo.detectedHeaders.length <
          parseResponse.data.length
      ) {
        return Promise.resolve({
          inputfileDetails: null,
          error:
            'The data rows have more columns than the available file headers.',
        });
      } else if (
        (parseResponse.headersInfo.hasHeaders &&
          parseResponse.headersInfo.actualHeaders.length >
            parseResponse.data.length) ||
        parseResponse.headersInfo.detectedHeaders.length >
          parseResponse.data.length
      ) {
        return Promise.resolve({
          inputfileDetails: null,
          error:
            'The data rows have less columns than the available file headers.',
        });
      } else if (!headersInfo.hasHeaders || hasEmptyHeaders) {
        return Promise.resolve({
          inputfileDetails: null,
          error:
            'A header was not detected or is not complete. All fields must have a header value. Please check the input file.',
        });
      }
      const uniqueHeaders: string[] = [];
      parseResponse.headersInfo.actualHeaders.map((header) => {
        if (uniqueHeaders.includes(header.toLowerCase())) {
          throw new Error('Selected file has duplicate header(s).');
        } else {
          uniqueHeaders.push(header.toLowerCase());
        }
      });
      const newInputFileDetails: InputFileDetails = {
        inputFile: file,
        ...parseResponse,
        experianFileName: experianFileName,
        clientFileName: clientFileName,
        recordsExpected: totalRecords,
        intentType: intentType,
        fileId: fileId,
      };
      if (headersInfo.hasHeaders) {
        return fieldHeadersValidator
          .validate(headersInfo.actualHeaders)
          .then(() => {
            return Promise.resolve({
              inputfileDetails: newInputFileDetails,
              error: '',
            });
          })
          .catch(() => {
            return Promise.resolve({
              inputfileDetails: newInputFileDetails,
              error: '',
            });
          });
      } else {
        return Promise.resolve({
          inputfileDetails: newInputFileDetails,
          error: '',
        });
      }
    })
    .catch((err) => {
      return Promise.reject(err);
    });
};
